import React from 'react';
import { MessageSquare, Users, Hash, TrendingUp, Award, Zap } from 'lucide-react';
import { COMMUNITY_STATS, formatNumber } from '@/lib/mockData';
import Icon from '@/components/ui/AppIcon';


const stats = [
  {
    id: 'stat-total-messages',
    label: 'Total Messages',
    value: formatNumber(COMMUNITY_STATS?.totalMessages ?? 0),
    rawValue: COMMUNITY_STATS?.totalMessages ?? 0,
    icon: MessageSquare,
    trend: '+12.4%',
    trendUp: true,
    accent: '#22c55e',
    description: 'All time',
    size: 'hero',
  },
  {
    id: 'stat-active-users',
    label: 'Active Users',
    value: (COMMUNITY_STATS?.activeUsers ?? 0)?.toString(),
    rawValue: COMMUNITY_STATS?.activeUsers ?? 0,
    icon: Users,
    trend: '+8 today',
    trendUp: true,
    accent: '#8b5cf6',
    description: 'Right now',
    size: 'normal',
  },
  {
    id: 'stat-total-groups',
    label: 'Total Groups',
    value: (COMMUNITY_STATS?.totalGroups ?? 0)?.toString(),
    rawValue: COMMUNITY_STATS?.totalGroups ?? 0,
    icon: Hash,
    trend: '+3 this week',
    trendUp: true,
    accent: '#06b6d4',
    description: 'Active communities',
    size: 'normal',
  },
  {
    id: 'stat-week-messages',
    label: 'Messages This Week',
    value: formatNumber(COMMUNITY_STATS?.messagesThisWeek ?? 0),
    rawValue: COMMUNITY_STATS?.messagesThisWeek ?? 0,
    icon: TrendingUp,
    trend: '+22.1% vs last week',
    trendUp: true,
    accent: '#f59e0b',
    description: 'This week',
    size: 'normal',
  },
  {
    id: 'stat-top-scorer',
    label: 'Top Scorer',
    value: COMMUNITY_STATS?.topScorer?.displayName ?? '—',
    rawValue: 0,
    icon: Award,
    trend: COMMUNITY_STATS?.topScorer ? `${formatNumber(COMMUNITY_STATS?.topScorer?.score)} pts` : 'No entries yet',
    trendUp: true,
    accent: '#f59e0b',
    description: '#1 Overall',
    size: 'normal',
    isUser: !!COMMUNITY_STATS?.topScorer,
    userInitials: COMMUNITY_STATS?.topScorer?.initials ?? '',
    userColor: COMMUNITY_STATS?.topScorer?.avatarColor ?? '#6b7280',
  },
  {
    id: 'stat-fastest-riser',
    label: 'Fastest Rising',
    value: COMMUNITY_STATS?.fastestRiser?.displayName ?? '—',
    rawValue: 0,
    icon: Zap,
    trend: COMMUNITY_STATS?.fastestRiser ? `+${COMMUNITY_STATS?.fastestRiser?.weeklyGrowth}% this week` : 'No entries yet',
    trendUp: true,
    accent: '#22c55e',
    description: 'Biggest mover',
    size: 'normal',
    isUser: !!COMMUNITY_STATS?.fastestRiser,
    userInitials: COMMUNITY_STATS?.fastestRiser?.initials ?? '',
    userColor: COMMUNITY_STATS?.fastestRiser?.avatarColor ?? '#6b7280',
  },
];

export default function HeroStats() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 2xl:grid-cols-6 gap-4">
      {stats?.map((stat, i) => {
        const Icon = stat?.icon;
        const isHero = i === 0;
        return (
          <div
            key={stat?.id}
            className={`card-glass relative overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer group ${
              isHero
                ? 'sm:col-span-2 lg:col-span-2 xl:col-span-2 2xl:col-span-2' :'col-span-1'
            }`}
            style={{
              padding: isHero ? '28px' : '20px',
              borderColor: `${stat?.accent}20`,
            }}
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
              style={{
                background: `radial-gradient(ellipse at top left, ${stat?.accent}10 0%, transparent 70%)`,
              }}
            />
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-3">
                <div
                  className="p-2 rounded-lg"
                  style={{ background: `${stat?.accent}15` }}
                >
                  <Icon size={isHero ? 22 : 18} style={{ color: stat?.accent }} />
                </div>
                {stat?.trendUp && (
                  <span
                    className="text-xs font-semibold px-2 py-1 rounded-full"
                    style={{
                      background: `${stat?.accent}15`,
                      color: stat?.accent,
                    }}
                  >
                    {stat?.trend}
                  </span>
                )}
              </div>

              {stat?.isUser ? (
                <div className="flex items-center gap-3 mt-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                    style={{ background: stat?.userColor, color: '#fff' }}
                  >
                    {stat?.userInitials}
                  </div>
                  <div>
                    <p
                      className={`font-bold leading-tight ${isHero ? 'text-2xl' : 'text-lg'}`}
                      style={{ color: 'var(--foreground)' }}
                    >
                      {stat?.value}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                      {stat?.description}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <p
                    className={`text-tabular font-extrabold leading-none ${isHero ? 'text-metric' : 'text-3xl'}`}
                    style={{ color: 'var(--foreground)' }}
                  >
                    {stat?.value}
                  </p>
                  <p className="text-xs mt-1.5 font-medium" style={{ color: 'var(--muted-foreground)' }}>
                    {stat?.description}
                  </p>
                </div>
              )}

              <p
                className="text-xs font-semibold mt-3 tracking-wide uppercase"
                style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}
              >
                {stat?.label}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}